import { Component, inject, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { BusinessReserveChooseGroupDialogModel } from '../../../interfaces/dialogs/business-reserve-choose-group-dialog.model';
import { MatButton } from '@angular/material/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import {
  ExamInfo,
  ExamItemList,
} from '../../../interfaces/reserve-loading.model';
import { ComparePlanGroup } from '../../../interfaces/business-reserve-compare.model';
import { SharedStorageService } from '../../../services/shared-storage.service';
import { NumberToChinesePipe } from '../../../../pipes/number-to-chinese.pipe';
import { MatCheckbox } from "@angular/material/checkbox";
import { AppointmentTimeService } from '../../../services/appointment-time.service';
import { BusinessAvailableDatesRequest } from '../../../interfaces/appointment-time.model';
import { SingleActionDialogComponent } from '../single-action-dialog/single-action-dialog.component';
import { Router } from '@angular/router';

/**
 * 企業方案檢項組合選一dialog
 */
@Component({
  selector: 'app-business-reserve-choose-group-dialog',
  standalone: true,
  imports: [
    MatButton,
    TranslatePipe,
    MatRadioGroup,
    MatRadioButton,
    FormsModule,
    NumberToChinesePipe,
    MatDialogContent,
    MatCheckbox
],
  templateUrl: './business-reserve-choose-group-dialog.component.html',
  styleUrl: './business-reserve-choose-group-dialog.component.scss',
})
export class BusinessReserveChooseGroupDialogComponent {
  currentIndex = 0; // 當前顯示的項目索引
  currentGroupIndex = 0; // 當前顯示的組合索引
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  appointmentTimeService: AppointmentTimeService = inject(AppointmentTimeService);
  translateService: TranslateService = inject(TranslateService);
  router: Router = inject(Router);
  dialog: MatDialog = inject(MatDialog);
  selectedGroup?: ExamItemList; // 單選時使用
  selectedGroupIndexes: number[] = []; // 使用 index 來追蹤選擇的項目
  selectedGroups: any[] = []; // 儲存實際選擇的組合
  numberToChinesePipe: NumberToChinesePipe = new NumberToChinesePipe();
  groupSelections: Map<number, any> = new Map(); // 儲存每個組合的選擇
  selectedGroupIndex?: number; // 新增：單選時使用的索引

  constructor(
    public dialogRef: MatDialogRef<BusinessReserveChooseGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BusinessReserveChooseGroupDialogModel,
  ) {}

  // 確認組合檢項
  confirm() {
    const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
    const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
    const myReservation = this.sharedStorageService.get('myReservation');
    if (!businessSelectedPackage) return;

    if (this.data.group) {
      console.log("單組合模式");
      // 單組合模式
      this.saveCurrentGroupSelection();
    }

    // 更新選擇狀態後，再收集檢項
    const request: BusinessAvailableDatesRequest = {
      orgNo: businessPlanRequest?.orgNo ?? '',
      companyNo: businessPlanRequest?.companyNo ?? '',
      packageNo: businessSelectedPackage.packageNo ?? '',
      gender: businessPlanRequest?.gender ?? '',
      caseNo: myReservation?.caseNo ?? '', // 修改預約時必填
      dayType: businessSelectedPackage.dayType ?? '',
      bookPeriod: businessSelectedPackage.bookPeriod ?? '',
      examItemList: this.buildSimpleExamItemList(),
      addList: [],
    };

    this.appointmentTimeService.getBusinessAvailableDates(request).subscribe(res => {
      console.log('res', res);

      // 若有可預約人數
      if (res.data && res.data.bookCalInfos && res.data.nutCalInfos) {
        const allBookCntZero = res.data.bookCalInfos.every(info => Number(info.bookCnt) === 0);
        console.log('allBookCntZero', allBookCntZero);
        const allNutCntZero = res.data.nutCalInfos.every(info => Number(info.nutBookCnt) === 0);
        console.log('allNutCntZero', allNutCntZero);

        // 若所有可預約人數都為 0
        if (allBookCntZero && allNutCntZero) {
          console.log('所有可預約人數為 0，重新選擇');
          const dialog = this.dialog.open(SingleActionDialogComponent, {
            data: {
              title: this.translateService.instant('business-reserve-compare-choose-dialog.dialog-title'),
              message: this.translateService.instant('business-reserve-compare-choose-dialog.dialog-message'),
              buttonText: this.translateService.instant('business-reserve-compare-choose-dialog.dialog-reselect'),
              showCloseButton: false,
              action: () => {
                dialog.close();
                this.router.navigate(['/healthcheck/reserve/companyplan']).then();
              }
            },
          });
          return;
        } else {
          this.router.navigate(['/healthcheck/reserve/additem']).then();
        }
      }
    });

    this.buildExamItemList();
    this.sharedStorageService.set('businessSelectedPackage', businessSelectedPackage);
    // 刪除第二跟第三比較方案
    this.sharedStorageService.remove('secondComparedPackage');
    this.sharedStorageService.remove('thirdComparedPackage');
    this.dialogRef.close(businessSelectedPackage);
  }

  // 關閉dialog
  closeDialog(): void {
    this.dialogRef.close();
  }

  get itemList(): Array<any> {
    const group = this.currentGroup;
    
    if (group?.groupList) {
      return group.groupList;
    } else if (group?.examItemList) {
      return group.examItemList.map((exam: any) => ({ examItemList: [exam] }));
    }
    
    return [];
  }

  // 取得中文標題
  getChineseTitle(): string {
    let titleName = '';
    
    if (this.needsPagination && this.data.groups) {
      // 多選情況：從當前組合取得標題
      titleName = this.data.groups[this.currentGroupIndex]?.groupTitleName || '';
    } else if (this.data.group) {
      // 單選情況：從單一組合取得標題
      titleName = this.data.group.groupTitleName || '';
    }
    
    return titleName
      .replace(/組合\s*/g, '')
      .replace(/(\d+)/g, (match) => 
        this.numberToChinesePipe.transform(parseInt(match))
      );
  }
  
  // 判斷是否為多選
  get isMultipleChoice(): boolean {
    return this.currentGroup?.chooseCount > 1;
  }

  // 取得當前組合
  get currentGroup(): any {
    if (this.data.groups && this.data.groups.length > 0) {
      return this.data.groups[this.currentGroupIndex];
    }
    return this.data.group;
  }

  // 處理多選變更
  onCheckboxChange(checked: boolean, item: any, index: number) {
    if (checked) {
      if (this.selectedGroupIndexes.length < this.currentGroup.chooseCount) {
        this.selectedGroupIndexes.push(index);
        this.selectedGroups.push(item);
      }
    } else {
      const idx = this.selectedGroupIndexes.indexOf(index);
      if (idx > -1) {
        this.selectedGroupIndexes.splice(idx, 1);
        this.selectedGroups.splice(idx, 1);
      }
    }
    
    // 立即儲存當前選擇狀態
    if (this.needsPagination) {
      this.saveCurrentGroupSelection();
    }
  }

  // 檢查是否已選擇
  isSelected(index: number): boolean {
    return this.selectedGroupIndexes.includes(index);
  }

  // 確認按鈕是否可用
  get canConfirm(): boolean {
    if (this.needsPagination) {
      // 多組合模式：檢查所有組合都已選擇
      for (let i = 0; i < this.totalGroups; i++) {
        if (!this.groupSelections.has(i)) {
          return false;
        }
      }
      return true;
    } else {
      // 單組合模式
      if (this.isMultipleChoice) {
        return this.selectedGroups.length === this.currentGroup.chooseCount;
      } else {
        return !!this.selectedGroup;
      }
    }
  }

  // 判斷是否需要分頁顯示
  get needsPagination(): boolean {
    return this.data.totalGroupCount > 1;
  }

  // 取得當前顯示的項目
  get currentItem(): any {
    const group = this.currentGroup;
    if (group?.groupList) {
      return group.groupList[this.currentIndex];
    } else if (group?.examItemList) {
      return { examItemList: [group.examItemList[this.currentIndex]] };
    }
    return null;
  }

  // 取得總項目數
  get totalItems(): number {
    const group = this.currentGroup;
    if (group?.groupList) {
      return group.groupList.length;
    } else if (group?.examItemList) {
      return group.examItemList.length;
    }
    return 0;
  }

  // 上一步
  previous(): void {
    if (this.needsPagination) {
      console.log(`從組合 ${this.currentGroupIndex} 按上一步`);
      console.log('當前選擇狀態:', { selectedGroups: this.selectedGroups, selectedGroup: this.selectedGroup });
      
      // 多組合模式：儲存當前選擇，然後切換到上一個組合
      this.saveCurrentGroupSelection();
      console.log('儲存後的 groupSelections:', this.groupSelections);
      
      if (this.currentGroupIndex > 0) {
        this.currentGroupIndex--;
        console.log(`切換到組合 ${this.currentGroupIndex}`);
        this.loadGroupSelection(); // 載入上一個組合的選擇狀態
      }
    }
  }

  // 下一步
  next(): void {
    // 多組合模式：儲存當前組合的選擇，然後切換到下一個組合
    this.saveCurrentGroupSelection();
    
    if (this.currentGroupIndex < this.totalGroups - 1) {
      this.currentGroupIndex++;
      this.loadGroupSelection(); // 載入下一個組合的選擇狀態
    } else {
      console.log('最後一個組合，執行確認');
      // 最後一個組合，執行確認
      this.confirm();
    }
  }

  // 儲存當前組合的選擇
  private saveCurrentGroupSelection(): void {
    const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
    if (!businessSelectedPackage) return;

    if (this.isMultipleChoice) {
      this.groupSelections.set(this.currentGroupIndex, {
        selectedIndexes: [...this.selectedGroupIndexes],
        selectedItems: [...this.selectedGroups]
      });
      console.log("this.groupSelections:", this.groupSelections);
    } else {
      this.groupSelections.set(this.currentGroupIndex, {
        selectedItem: this.selectedGroup
      });
      console.log("this.groupSelections:", this.groupSelections);
    }

    // 儲存更新後的 businessSelectedPackage
    this.sharedStorageService.set('businessSelectedPackage', businessSelectedPackage);

    // 從 groupSelections 收集所有選擇的檢項
    const examItemList: ExamInfo[] = [];
    this.groupSelections.forEach((selection) => {
      if (selection.selectedItems) {
        // 多選模式
        selection.selectedItems.forEach((item: any) => {
          item.examItemList?.forEach((exam: any) => {
            examItemList.push({
              examItem: exam.examItem,
              examName: exam.examName,
              examMemo: exam.examMemo || '',
              examPrice: exam.examPrice || '0',
              selectFlag: 1
            });
          });
        });
      } else if (selection.selectedItem) {
        // 單選模式
        selection.selectedItem.examItemList?.forEach((exam: any) => {
          examItemList.push({
            examItem: exam.examItem,
            examName: exam.examName,
            examMemo: exam.examMemo || '',
            examPrice: exam.examPrice || '0',
            selectFlag: 1
          });
        });
      }
    });

    this.sharedStorageService.set('examItemList', examItemList);
  }

  
  // 載入組合的選擇狀態
  private loadGroupSelection(): void {
    const selection = this.groupSelections.get(this.currentGroupIndex);
    
    if (selection) {
      if (this.isMultipleChoice) {
        // 多選模式處理...
        this.selectedGroupIndexes = [];
        this.selectedGroups = [];
        
        selection.selectedItems.forEach((savedItem: any) => {
          const index = this.itemList.findIndex(item => 
            item.examItemList?.[0]?.examItem === savedItem.examItemList?.[0]?.examItem
          );
          if (index !== -1) {
            this.selectedGroupIndexes.push(index);
            this.selectedGroups.push(this.itemList[index]);
          }
        });
      } else {
        // 單選模式：找到對應的索引
        const savedExamItem = selection.selectedItem?.examItemList?.[0]?.examItem;
        const foundIndex = this.itemList.findIndex(item => 
          item.examItemList?.[0]?.examItem === savedExamItem
        );
        
        if (foundIndex !== -1) {
          this.selectedGroupIndex = foundIndex;
          this.selectedGroup = this.itemList[foundIndex];
        } else {
          this.selectedGroupIndex = undefined;
          this.selectedGroup = undefined;
        }
      }
    } else {
      // 清空選擇狀態
      this.selectedGroupIndexes = [];
      this.selectedGroups = [];
      this.selectedGroup = undefined;
      this.selectedGroupIndex = undefined;
    }
  }

  // 是否可以上一步
  get canPrevious(): boolean {
    if (this.needsPagination) {
      return this.currentGroupIndex > 0;
    } else {
      return this.currentIndex > 0;
    }
  }

  //是否可以下一步
  get canNext(): boolean {
    if (this.isMultipleChoice) {
      return this.selectedGroups.length === this.currentGroup.chooseCount;
    } else {
      return !!this.selectedGroup;
    }
  }

  // 取得總組合數
  get totalGroups(): number {
    return this.data.groups ? this.data.groups.length : 1;
  }

  // 建立檢項清單，包含原本檢項和選擇的組合檢項，儲存到sharedStorage
  private buildExamItemList(): ExamItemList[] {
    const examItemList: ExamInfo[] = [];
    const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');

    console.log("businessSelectedPackage:", businessSelectedPackage);
    
    if (!businessSelectedPackage) return examItemList;

    // 1. 加入原本的 deptList 檢項
    businessSelectedPackage.deptList?.forEach((dept: any) => {
      dept.examItemList?.forEach((exam: any) => {
        examItemList.push({
          deptName: dept.deptName,
          examName: exam.examName,
          examItem: exam.examItem,
          examMemo: exam.examMemo || '',
          examPrice: exam.examPrice || '0',
          selectFlag: 1
        });
      });
    });

    // 2. 加入選擇的組合檢項
    this.groupSelections.forEach((selection) => {
      if (selection.selectedItems) {
        // 多選模式
        selection.selectedItems.forEach((item: any) => {
          item.examItemList?.forEach((exam: any) => {
            examItemList.push({
              deptName: '組合',
              deptEName: exam.deptEName,
              examName: exam.examName,
              examItem: exam.examItem,
              examMemo: exam.examMemo || '',
              examPrice: exam.examPrice || '0',
              selectFlag: 1
            });
          });
        });
      } else if (selection.selectedItem) {
        // 單選模式
        selection.selectedItem.examItemList?.forEach((exam: any) => {
          examItemList.push({
            deptName: '組合',
            deptEName: exam.deptEName,
            examName: exam.examName,
            examItem: exam.examItem,
            examMemo: exam.examMemo || '',
            examPrice: exam.examPrice || '0',
            selectFlag: 1
          });
        });
      }
    });

    this.sharedStorageService.set("examItemList", examItemList);

    return examItemList;
  }

  // 建立簡化的檢項清單，只包含 examItem 和 examName
  private buildSimpleExamItemList(): { examItem: string; examName: string }[] {
    const examItemList: { examItem: string; examName: string }[] = [];
    const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
    
    if (!businessSelectedPackage) return examItemList;

    // 1. 加入原本的 deptList 檢項
    businessSelectedPackage.deptList?.forEach((dept: any) => {
      dept.examItemList?.forEach((exam: any) => {
        examItemList.push({
          examItem: exam.examItem,
          examName: exam.examName
        });
      });
    });

    // 2. 根據單多組合模式加入選擇的檢項
    if (this.needsPagination && this.data.groups) {
      // 多組合模式：從 groupSelections 收集
      this.groupSelections.forEach((selection) => {
        if (selection.selectedItems) {
          selection.selectedItems.forEach((item: any) => {
            item.examItemList?.forEach((exam: any) => {
              examItemList.push({
                examItem: exam.examItem,
                examName: exam.examName
              });
            });
          });
        } else if (selection.selectedItem) {
          selection.selectedItem.examItemList?.forEach((exam: any) => {
            examItemList.push({
              examItem: exam.examItem,
              examName: exam.examName
            });
          });
        }
      });
    } else if (this.data.group) {
      // 單組合模式：從當前選擇收集
      const selectedItems = this.isMultipleChoice ? this.selectedGroups : [this.selectedGroup];
      selectedItems.forEach((item: any) => {
        item?.examItemList?.forEach((exam: any) => {
          examItemList.push({
            examItem: exam.examItem,
            examName: exam.examName
          });
        });
      });
    }
    return examItemList;
  }

  // 處理單選變更
  onRadioChange(selectedIndex: number) {
    this.selectedGroupIndex = selectedIndex;
    this.selectedGroup = this.itemList[selectedIndex];
    
    // 立即儲存當前選擇狀態
    if (this.needsPagination) {
      this.saveCurrentGroupSelection();
    }
  }
}
